import CustomerAuditEntity from 'Models/Entities/CustomerAuditEntity';
import { store } from 'Models/Store';
import { customerAuditCategory } from 'Models/Enums';
import { CustomerEntity } from 'Models/Entities';
import { isEqual } from 'lodash';
import moment from 'moment';
import { CombinedCustomerAuditCategory, CustomerAuditDescription } from 'Hooks/Api/apiCustomerAudits';

export interface CustomerAuditFilterParams {
	category: CombinedCustomerAuditCategory | 'ALL';
	dateRange: Date[] | undefined;
}

export const CustomerAuditFilterParamsDefault: CustomerAuditFilterParams = {
	category: 'ALL',
	dateRange: undefined,
}

export const CUSTOMER_AUDITS_PAGE_LENGTH = 10;

export const saveCustomerAudit = async (
	customerId: string,
	description: CustomerAuditDescription,
	category: customerAuditCategory,
) => {
	const customerAuditEntity = CreateCustomerAudit(customerId, category, description);
	await customerAuditEntity.save();
};

function AddChangedSection(
	changedSection: {section: string, previousValue: string, currentValue: string}[],
	section: string,
	previousValue: string,
	currentValue: string,
) {
	if (!isEqual(previousValue, currentValue)) {
		changedSection.push({
			section,
			previousValue,
			currentValue,
		});
	}
}

function getAddress(details: CustomerEntity, type: string) {
	const address = details[type];

	const response = [
		address?.line1,
		address?.line2,
		address?.suburb,
		address?.state,
		address?.postcode,
		address?.country,
	].filter(Boolean).join(' ');
	return response;
}

export function GetCustomerSingleAuditDescriptionFromString(
	previousValue: string,
	currentValue: string,
	section: string,
): CustomerAuditDescription {
	const parsedPreviousValue = previousValue && previousValue.length > 0 ? previousValue : 'N/A';
	const parsedCurrentValue = currentValue && currentValue.length > 0 ? currentValue : 'N/A';

	return {
		title: `${section} updated from ${parsedPreviousValue} to ${parsedCurrentValue}`,
		message: undefined,
	};
}

export function GetCustomerAuditsDescription(
	previousDetails: CustomerEntity,
	currentDetails: CustomerEntity,
	category: customerAuditCategory,
): CustomerAuditDescription | undefined {
	const changedSection: {section: string, previousValue: string, currentValue: string}[] = [];

	switch (category) {
		case 'ACCOUNT_INFORMATION': {
			AddChangedSection(changedSection, 'Credit limit', previousDetails.creditLimit, currentDetails.creditLimit);
			AddChangedSection(
				changedSection,
				'Payment terms',
				previousDetails.tradingTerms,
				currentDetails.tradingTerms,
			);
			AddChangedSection(
				changedSection,
				'Name for Invoice',
				previousDetails.nameForInvoice,
				currentDetails.nameForInvoice,
			);
			AddChangedSection(
				changedSection,
				'Accounts contact',
				previousDetails.accountsContact,
				currentDetails.accountsContact,
			);
			AddChangedSection(
				changedSection,
				'Accounts phone number',
				previousDetails.accountsPhoneNumber,
				currentDetails.accountsPhoneNumber,
			);
			AddChangedSection(
				changedSection,
				'Tax invoice email',
				previousDetails.taxInvoicesEmail,
				currentDetails.taxInvoicesEmail,
			);
			AddChangedSection(
				changedSection,
				'Statement email',
				previousDetails.statementsEmail,
				currentDetails.statementsEmail,
			);

			const previousPhysicalAddress = getAddress(previousDetails, 'physicalAddress');
			const currentPhysicalAddress = getAddress(currentDetails, 'physicalAddress');
			AddChangedSection(changedSection, 'Physical Address', previousPhysicalAddress, currentPhysicalAddress);

			const previousPostalAddress = getAddress(previousDetails, 'postalAddress');
			const currentPostalAddress = getAddress(currentDetails, 'postalAddress');
			AddChangedSection(changedSection, 'Postal address', previousPostalAddress, currentPostalAddress);
			break;
		}

		case 'BUSINESS_INFORMATION': {
			AddChangedSection(
				changedSection,
				'Business Name',
				previousDetails.businessName,
				currentDetails.businessName,
			);

			AddChangedSection(
				changedSection,
				'Debtor ID',
				previousDetails.debtorID,
				currentDetails.debtorID,
			);

			AddChangedSection(
				changedSection,
				"Customer's First Name",
				previousDetails.firstName,
				currentDetails.firstName,
			);

			AddChangedSection(
				changedSection,
				"Customer's Last Name",
				previousDetails.lastName,
				currentDetails.lastName,
			);

			const previousDateOfBirth = previousDetails.dateOfBirth
				? moment(previousDetails.dateOfBirth).format('DD/MM/YYYY')
				: '';
			const currentDateOfBirth = currentDetails.dateOfBirth
				? moment(currentDetails.dateOfBirth).format('DD/MM/YYYY')
				: '';
			AddChangedSection(
				changedSection,
				'Date of Birth',
				previousDateOfBirth,
				currentDateOfBirth,
			);

			const previousGroups = [...new Set(previousDetails.groupss.map(x => x.groups.name))].join(', ');
			const currentGroups = [...new Set(currentDetails.groupss.map(x => x.groups.name))].join(', ');
			AddChangedSection(
				changedSection,
				'Groups',
				previousGroups,
				currentGroups,
			);

			AddChangedSection(
				changedSection,
				'ABN',
				previousDetails.abn,
				currentDetails.abn,
			);

			AddChangedSection(
				changedSection,
				'ACN',
				previousDetails.acn,
				currentDetails.acn,
			);

			AddChangedSection(
				changedSection,
				'Trustee ACN',
				previousDetails.trusteeACN,
				currentDetails.trusteeACN,
			);

			AddChangedSection(
				changedSection,
				'Liquor Licence Number',
				previousDetails.drinksLicence,
				currentDetails.drinksLicence,
			);
			AddChangedSection(
				changedSection,
				'Liquor Licence State',
				previousDetails.drinksLicenceState,
				currentDetails.drinksLicenceState,
			);
			break;
		}
	}

	if (changedSection.length === 0) {
		return undefined;
	}

	if (changedSection.length === 1) {
		const { previousValue, currentValue } = changedSection[0];

		const parsedPreviousValue = previousValue && previousValue.length > 0 ? previousValue : 'N/A';
		const parsedCurrentValue = currentValue && currentValue.length > 0 ? currentValue : 'N/A';

		return {
			title: `${changedSection[0].section} updated from ${parsedPreviousValue} to ${parsedCurrentValue}`,
			message: undefined,
		};
	}

	return {
		title: 'Multiple Fields Updated',
		// eslint-disable-next-line max-len
		message: `The following fields have been updated:\n${GetMessageForCustomerAuditDescription(changedSection)}`,
	};
}

export function GetMessageForCustomerAuditDescription(
	array: {section: string, previousValue: string, currentValue: string}[],
): string | undefined {
	if (array.length === 0) {
		return undefined;
	}

	let result: string = '';

	array.forEach(item => {
		const parsedPreviousValue = item.previousValue && item.previousValue.length > 0 ? item.previousValue : 'N/A';
		const parsedCurrentValue = item.currentValue && item.currentValue.length > 0 ? item.currentValue : 'N/A';

		result = result.concat(`${item.section}\n  From ${parsedPreviousValue}\n  To ${parsedCurrentValue}\n\n`);
	});

	if (result.length > 0) {
		return result;
	}

	return undefined;
}

export function CreateCustomerAudit(
	customerId: string,
	category: customerAuditCategory,
	description: CustomerAuditDescription,
): CustomerAuditEntity {
	return new CustomerAuditEntity({
		customerId,
		category,
		description: JSON.stringify(description, (_, value) => {
			if (typeof value === 'undefined') {
				return null;
			}
			return value;
		}),
		userId: store.userId,
	});
}
